/**
 * Persistência do oplog e do estado do sync sobre op-sqlite (tabelas `sync_messages` e
 * `sync_state`, migração 0021). O motor (engine.ts) é puro e trabalha em Message[]; estas
 * funções carregam/gravam esse array e o relógio HLC entre boots. Ver docs/features/SYNC.md § 3.
 *
 * NÃO testável no Jest (op-sqlite é nativo) — verificado pelo harness no device
 * (lib/sync/selfTest.ts) e, adiante, pelos testes de integração no emulador.
 */

import { execute } from '@/db/client';
import type { EnvelopeVersion } from '@/lib/backupCrypto';
import { generateId } from '@/lib/id';
import { Hlc, makeNode } from './hlc';
import { type Message, TOMBSTONE_COLUMN } from './messages';

const DEVICE_ID_KEY = 'device_id';
const HLC_MILLIS_KEY = 'hlc_millis';
const HLC_COUNTER_KEY = 'hlc_counter';

// ─── sync_state (k/v) ────────────────────────────────────────

async function getState(key: string): Promise<string | null> {
    const r = await execute('SELECT value FROM sync_state WHERE key = ?', [key]);
    const v = r.rows[0]?.value;
    return v == null ? null : String(v);
}

async function setState(key: string, value: string): Promise<void> {
    await execute('INSERT OR REPLACE INTO sync_state (key, value) VALUES (?, ?)', [key, value]);
}

/** Id estável desta instalação — o nó do HLC. Criado uma vez (CSPRNG) e persistido. */
export async function getOrCreateDeviceId(): Promise<string> {
    const existing = await getState(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = generateId();
    await setState(DEVICE_ID_KEY, id);
    return id;
}

/** Relógio HLC pronto pra usar: nó = deviceId, estado (millis/counter) restaurado do disco. */
export async function loadHlc(): Promise<Hlc> {
    const deviceId = await getOrCreateDeviceId();
    const millis = Number((await getState(HLC_MILLIS_KEY)) ?? '0');
    const counter = Number((await getState(HLC_COUNTER_KEY)) ?? '0');
    return new Hlc(deviceId, { initial: { millis, counter, node: makeNode(deviceId) } });
}

/** Persiste o estado do relógio (chamar após drenar/aplicar, senão os HLCs regridem no boot). */
export async function persistHlc(hlc: Hlc): Promise<void> {
    const s = hlc.state();
    await setState(HLC_MILLIS_KEY, String(s.millis));
    await setState(HLC_COUNTER_KEY, String(s.counter));
}

// ─── sync_messages (oplog) ───────────────────────────────────

/** Valor pronto pro bind do op-sqlite (não aceita boolean; tombstone vira 0/1). */
function valueForDb(m: Message): string | number | null {
    if (m.column === TOMBSTONE_COLUMN) return m.value ? 1 : 0;
    return (m.value as string | number | null) ?? null;
}

function rowToMessage(row: Record<string, unknown>): Message {
    const column = String(row.column_name);
    return {
        timestamp: String(row.timestamp),
        table: String(row.table_name),
        row: String(row.row_id),
        column,
        value: column === TOMBSTONE_COLUMN ? Boolean(row.value) : (row.value ?? null),
    };
}

/** Todo o oplog local (pra materializar/recompactar). */
export async function loadMessages(): Promise<Message[]> {
    const r = await execute(
        'SELECT table_name, row_id, column_name, timestamp, value FROM sync_messages',
    );
    return r.rows.map(rowToMessage);
}

/** Mensagens de UMA linha, pra materializá-la sem carregar o oplog inteiro. */
export async function loadRowMessages(table: string, row: string): Promise<Message[]> {
    const r = await execute(
        'SELECT table_name, row_id, column_name, timestamp, value FROM sync_messages WHERE table_name = ? AND row_id = ?',
        [table, row],
    );
    return r.rows.map(rowToMessage);
}

/**
 * Mensagens locais AINDA NÃO confirmadas no Drive (pending_upload=1, migração 0025) — o dreno
 * marca, o ciclo sobe e só desmarca após o upload dar certo. Ordenadas por HLC (o packed é
 * lexicograficamente ordenável), preservando a causalidade local no segmento.
 */
export async function loadPendingUpload(): Promise<Message[]> {
    const r = await execute(
        'SELECT table_name, row_id, column_name, timestamp, value FROM sync_messages WHERE pending_upload = 1 ORDER BY timestamp',
    );
    return r.rows.map(rowToMessage);
}

/**
 * Há escrita local esperando pra subir? Na outbox (ainda não drenada) ou no oplog marcada
 * `pending_upload` (drenada, mas o Drive ainda não aceitou). Os dois EXISTS param na 1ª linha —
 * a outbox esvazia a cada ciclo e `pending_upload` tem índice —, então é barato de chamar a cada
 * escrita.
 */
export async function hasPendingSyncWork(): Promise<boolean> {
    const r = await execute(
        'SELECT (EXISTS(SELECT 1 FROM sync_outbox) OR EXISTS(SELECT 1 FROM sync_messages WHERE pending_upload = 1)) AS has',
    );
    return Number(r.rows[0]?.has ?? 0) > 0;
}

/** Confirma o upload: desmarca TUDO que estava pendente (chamar só após o Drive aceitar). */
export async function clearPendingUpload(): Promise<void> {
    await execute('UPDATE sync_messages SET pending_upload = 0 WHERE pending_upload = 1');
}

/**
 * Compacta o oplog LOCAL (M3, auditoria jul/2026): por célula (tabela,linha,coluna) o LWW só
 * precisa da mensagem VENCEDORA (maior HLC) — as anteriores são peso morto que nunca vão ganhar.
 * Sem isto o `sync_messages` cresce a cada escrita PARA SEMPRE, e `loadMessages`/`materialize`
 * (usados no apply e no seed-diff) ficam mais lentos a cada boot. Preserva os pendentes de upload
 * (`pending_upload=1`: ainda precisam subir mesmo já superados localmente) e sempre mantém o
 * vencedor de cada célula. Idempotente. NÃO toca no Drive — compactar os SEGMENTOS remotos é
 * outra história (precisa de snapshot + GC por cursor publicado; ver ADR 0039 § trabalho futuro).
 */
export async function compactOplog(): Promise<void> {
    await execute(
        `DELETE FROM sync_messages
         WHERE pending_upload = 0
           AND timestamp < (
               SELECT MAX(m2.timestamp) FROM sync_messages m2
               WHERE m2.table_name = sync_messages.table_name
                 AND m2.row_id = sync_messages.row_id
                 AND m2.column_name = sync_messages.column_name
           )`,
    );
}

// ─── Sequência do próprio oplog + cursores por peer ──────────

/** Próximo número de segmento DESTE aparelho (append-only, monotônico). */
export async function nextOwnSegmentSeq(): Promise<number> {
    const next = Number((await getState('oplog_seq')) ?? '0') + 1;
    await setState('oplog_seq', String(next));
    return next;
}

/** Prefixo das chaves de cursor no sync_state — única fonte do esquema `cursor_<deviceId>`. */
const CURSOR_KEY_PREFIX = 'cursor_';

/** Até qual seq já consumimos os segmentos de um peer (0 = nenhum). */
export async function getCursor(deviceId: string): Promise<number> {
    return Number((await getState(`${CURSOR_KEY_PREFIX}${deviceId}`)) ?? '0');
}

export async function setCursor(deviceId: string, seq: number): Promise<void> {
    await setState(`${CURSOR_KEY_PREFIX}${deviceId}`, String(seq));
}

/** Cursores de TODOS os peers conhecidos (deviceId → seq consumido) — o ciclo carrega UMA vez
 *  (em vez de 1 query por segmento) e a GC no Drive (ADR 0040) decide cobertura por eles: um
 *  segmento alheio só pode ser apagado se `seq ≤ cursor` (consumido ⟹ o conteúdo já está no
 *  oplog local, ou dominado por quem está — e portanto dentro do checkpoint). */
export async function getAllCursors(): Promise<Map<string, number>> {
    const r = await execute(
        `SELECT key, value FROM sync_state WHERE key LIKE '${CURSOR_KEY_PREFIX}%'`,
    );
    const cursors = new Map<string, number>();
    for (const row of r.rows) cursors.set(String(row.key).slice(CURSOR_KEY_PREFIX.length), Number(row.value));
    return cursors;
}

/**
 * Backoff da GC no Drive (ADR 0040): se uma rodada de deleção falhou POR COMPLETO (ex.: 403 de
 * permissão/quota persistente), sem isto o gatilho continuaria satisfeito e TODO ciclo re-subiria
 * o checkpoint inteiro sem nunca encolher a pasta — churn de banda/quota sem teto. O sucesso
 * (mesmo parcial) nunca agenda backoff.
 */
export async function getGcBackoffUntil(): Promise<number> {
    return Number((await getState('gc_backoff_until')) ?? '0');
}

export async function setGcBackoffUntil(epochMs: number): Promise<void> {
    await setState('gc_backoff_until', String(epochMs));
}

/** Já semeamos o oplog com os dados que já existiam quando o sync ligou neste aparelho? */
export async function isSeeded(): Promise<boolean> {
    return (await getState('seeded')) === '1';
}

export async function markSeeded(): Promise<void> {
    await setState('seeded', '1');
}

/**
 * Zera o flag de seed e os cursores dos peers (mantém deviceId, HLC e oplog_seq). Chamado ao
 * DESLIGAR o sync: um OFF→ON re-semeia os dados atuais e re-lê os peers do zero — o caminho de
 * recuperação se a chave/senha mudou. NÃO reseta oplog_seq (o próximo segmento tem que ter seq
 * MAIOR, senão os peers o ignoram pelo cursor deles). A versão negociada do envelope cai junto:
 * sem sync não há peer pra proteger, o backup volta a sair no formato mais compacto. Os sinais
 * de vida dos peers (`seen_*`) FICAM: religar no mesmo espaço precisa lembrar quem está nele.
 */
export async function resetSeedAndCursors(): Promise<void> {
    await execute(`DELETE FROM sync_state WHERE key = 'seeded' OR key = '${ENVELOPE_VERSION_KEY}' OR key LIKE '${CURSOR_KEY_PREFIX}%'`);
}

/**
 * Esquece o espaço de sync ANTERIOR sem tocar nos dados (redefinição — ADR 0191): oplog, outbox,
 * cursores, seed, sinais de vida dos peers, versão negociada e backoff da GC. Mantém o deviceId,
 * o HLC (o relógio só anda pra frente) e o `oplog_seq` (os próximos segmentos seguem numerados
 * acima dos antigos). Com o oplog vazio, o próximo `enableSync` semeia a base INTEIRA.
 */
export async function forgetSyncLineage(): Promise<void> {
    await execute('DELETE FROM sync_outbox');
    await execute('DELETE FROM sync_messages');
    await execute(
        `DELETE FROM sync_state WHERE key NOT IN ('${DEVICE_ID_KEY}', '${HLC_MILLIS_KEY}', '${HLC_COUNTER_KEY}', 'oplog_seq')`,
    );
}

/**
 * Id do meta do espaço a que o oplog local PERTENCE (ADR 0191), gravado pelo ciclo que conferiu a
 * chave. Fica no `sync_state` de propósito: desligar o sync apaga a chave mas não o oplog, e
 * religar depois de uma redefinição feita em outro aparelho não pode semear por um histórico de
 * outro espaço (ver `localOplogFromAnotherSpace`).
 */
export async function getLineageMetaId(): Promise<string | null> {
    return getState('lineage_meta_id');
}

export async function setLineageMetaId(metaId: string): Promise<void> {
    await setState('lineage_meta_id', metaId);
}

/** Instante (ms) em que ESTE aparelho redefiniu a sincronização, ou null. */
export async function getSpaceResetAt(): Promise<number | null> {
    const v = await getState('space_reset_at');
    return v ? Number(v) : null;
}

export async function markSpaceReset(epochMs: number): Promise<void> {
    await setState('space_reset_at', String(epochMs));
}

// ─── Negociação do envelope (ADR 0181) ───────────────────────

/** Prefixo das chaves `seen_<deviceId>` = último sinal de vida de um peer (ms desde a época). */
const SEEN_KEY_PREFIX = 'seen_';
const ENVELOPE_VERSION_KEY = 'envelope_write_version';

/** Último sinal de vida de cada peer conhecido (deviceId → ms). */
export async function getDeviceSeen(): Promise<Map<string, number>> {
    const r = await execute(`SELECT key, value FROM sync_state WHERE key LIKE '${SEEN_KEY_PREFIX}%'`);
    const seen = new Map<string, number>();
    for (const row of r.rows) seen.set(String(row.key).slice(SEEN_KEY_PREFIX.length), Number(row.value));
    return seen;
}

export async function setDeviceSeen(deviceId: string, epochMs: number): Promise<void> {
    await setState(`${SEEN_KEY_PREFIX}${deviceId}`, String(epochMs));
}

/**
 * Versão do envelope que o último ciclo negociou com o espaço (ADR 0181) — o segmento E o backup
 * saem nela. null = nunca negociou (sync nunca rodou aqui, ou foi desligado): escreve o máximo.
 */
export async function getNegotiatedEnvelopeVersion(): Promise<EnvelopeVersion | null> {
    const v = await getState(ENVELOPE_VERSION_KEY);
    return v === '1' ? 1 : v === '2' ? 2 : null;
}

export async function setNegotiatedEnvelopeVersion(version: EnvelopeVersion): Promise<void> {
    await setState(ENVELOPE_VERSION_KEY, String(version));
}

/**
 * Grava mensagens no oplog. Idempotente: PK (tabela,linha,coluna,HLC) + INSERT OR IGNORE.
 * `pendingUpload` marca mensagens LOCAIS recém-drenadas que ainda precisam subir ao Drive
 * (mensagens remotas aplicadas ficam com o default 0 — nunca re-sobem).
 */
export async function persistMessages(messages: Message[], opts?: { pendingUpload?: boolean }): Promise<void> {
    const pending = opts?.pendingUpload ? 1 : 0;
    // Em lotes: um INSERT por mensagem fazia o join de uma base grande (100 mil+ células) gastar
    // segundos só aqui. 100 linhas × 6 colunas = 600 parâmetros, folga sob qualquer limite do SQLite.
    for (let i = 0; i < messages.length; i += PERSIST_CHUNK) {
        const chunk = messages.slice(i, i + PERSIST_CHUNK);
        await execute(
            'INSERT OR IGNORE INTO sync_messages (table_name, row_id, column_name, timestamp, value, pending_upload) VALUES '
                + chunk.map(() => '(?, ?, ?, ?, ?, ?)').join(', '),
            chunk.flatMap((m) => [m.table, m.row, m.column, m.timestamp, valueForDb(m), pending]),
        );
    }
}
const PERSIST_CHUNK = 100;
